import React, { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckCircle, Mail, ArrowRight } from 'lucide-react'
import DatePicker from '../components/ui/DatePicker'
import Seo from '../components/Seo'

const TIME_SLOTS = (() => {
  const slots: { value: string; label: string }[] = []
  for (let hour = 8; hour <= 18; hour++) {
    for (const minute of [0, 30]) {
      if (hour === 18 && minute === 30) break
      const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
      const period = hour >= 12 ? 'PM' : 'AM'
      const displayHour = hour % 12 === 0 ? 12 : hour % 12
      const label = `${displayHour}:${String(minute).padStart(2, '0')} ${period}`
      slots.push({ value, label })
    }
  }
  return slots
})()

const tomorrowISO = () => {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  d.setHours(0, 0, 0, 0)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const formatDate = (value: string) => {
  const parsed = new Date(`${value}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

const formatTime = (value: string) => {
  const [hour, minute] = String(value || '').split(':')
  const h = Number(hour)
  if (Number.isNaN(h)) return value
  const period = h >= 12 ? 'PM' : 'AM'
  const displayHour = h % 12 === 0 ? 12 : h % 12
  return `${displayHour}:${minute} ${period} ET`
}

type RequestRecord = {
  id: string
  fullName: string
  email: string
  phone: string
  format: 'phone' | 'video' | string
  preferredDate: string
  preferredTime: string
  status: string
}

type ViewState = 'loading' | 'ready' | 'invalid' | 'needEmail'

const ManageConsultation: React.FC = () => {
  const [searchParams] = useSearchParams()
  const requestId = searchParams.get('requestId') || ''
  const token = searchParams.get('token') || ''

  const [view, setView] = useState<ViewState>(
    requestId && token ? 'loading' : 'needEmail'
  )
  const [record, setRecord] = useState<RequestRecord | null>(null)
  const [preferredDate, setPreferredDate] = useState('')
  const [preferredTime, setPreferredTime] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [cancelled, setCancelled] = useState(false)

  const [emailInput, setEmailInput] = useState('')
  const [emailSubmitted, setEmailSubmitted] = useState(false)
  const [emailLoading, setEmailLoading] = useState(false)
  const [emailError, setEmailError] = useState('')

  const minDate = useMemo(() => tomorrowISO(), [])

  useEffect(() => {
    if (!requestId || !token) return

    let cancelledRequest = false

    const verify = async () => {
      try {
        const response = await fetch('/api/consultation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'verifyManageToken',
            requestId,
            token,
          }),
        })

        const data = await response.json()

        if (cancelledRequest) return

        if (!response.ok || !data.ok) {
          setView('invalid')
          setError(
            data?.error ||
              'This link has expired or is no longer valid. Request a new one below.'
          )
          return
        }

        setRecord(data.request)
        setPreferredDate(data.request.preferredDate || '')
        setPreferredTime(data.request.preferredTime || '')
        setView('ready')
      } catch (err) {
        if (cancelledRequest) return
        console.error(err)
        setView('invalid')
        setError('Something went wrong verifying your link. Please try again.')
      }
    }

    verify()

    return () => {
      cancelledRequest = true
    }
  }, [requestId, token])

  const handleRequestLink = async (e: React.FormEvent) => {
    e.preventDefault()
    setEmailError('')

    if (!emailInput.trim()) {
      setEmailError('Please enter your email address.')
      return
    }

    setEmailLoading(true)

    try {
      await fetch('/api/consultation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'requestManageLink',
          email: emailInput.trim(),
        }),
      })

      setEmailSubmitted(true)
    } catch (err) {
      console.error(err)
      setEmailError('Something went wrong. Please try again.')
    } finally {
      setEmailLoading(false)
    }
  }

  const handleSave = async () => {
    if (!record) return
    setError('')

    if (!preferredDate || preferredDate < minDate) {
      setError('Please choose a date starting tomorrow or later.')
      return
    }

    if (!preferredTime) {
      setError('Please choose a preferred time.')
      return
    }

    setLoading(true)

    try {
      const response = await fetch('/api/consultation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: record.fullName,
          email: record.email,
          phone: record.phone,
          format: record.format,
          preferred_date: preferredDate,
          preferred_time: preferredTime,
          requestId: record.id,
          manageToken: token,
          isUpdate: true,
        }),
      })

      const data = await response.json()

      if (!response.ok || !data.ok) {
        throw new Error(data?.error || 'The update failed. Please try again.')
      }

      setSuccess(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      console.error(err)
      setError(
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again or email hello@fitbycassie.com.'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = async () => {
    if (!record) return

    const confirmed = window.confirm(
      'Are you sure you want to cancel this consultation request?'
    )
    if (!confirmed) return

    setLoading(true)
    setError('')

    try {
      const response = await fetch('/api/consultation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: record.fullName,
          email: record.email,
          requestId: record.id,
          manageToken: token,
          isCancel: true,
        }),
      })

      const data = await response.json()

      if (!response.ok || !data.ok) {
        throw new Error(
          data?.error || 'The cancellation failed. Please try again.'
        )
      }

      setCancelled(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      console.error(err)
      setError(
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again or email hello@fitbycassie.com.'
      )
    } finally {
      setLoading(false)
    }
  }

  // -------------------------------------------------------------------
  // Cancelled confirmation
  // -------------------------------------------------------------------
  if (cancelled) {
    return (
      <div className="min-h-screen bg-cream">
        <Seo
          title="Consultation Request Cancelled"
          description="Your consultation request with Cassie has been cancelled."
        />
        <section className="section-padding">
          <div className="container-max max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl shadow-soft border border-gray-100 p-10 sm:p-14"
            >
              <h1 className="heading-section mb-4">Request cancelled</h1>
              <p className="lead mb-8">
                Your consultation request has been cancelled. If you change
                your mind, you&apos;re welcome to submit a new request
                anytime.
              </p>
              <Link to="/" className="btn-primary">
                Back to Home
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    )
  }

  // -------------------------------------------------------------------
  // Update success
  // -------------------------------------------------------------------
  if (success && record) {
    return (
      <div className="min-h-screen bg-cream">
        <Seo
          title="Consultation Request Updated"
          description="Your consultation request with Cassie has been updated."
        />
        <section className="section-padding">
          <div className="container-max max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl shadow-soft border border-gray-100 p-10 sm:p-14"
            >
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center">
                <CheckCircle className="text-brand" size={32} />
              </div>
              <h1 className="heading-section mb-4">Request updated</h1>
              <p className="lead mb-8">
                Your new preferred time is{' '}
                <strong>
                  {formatDate(preferredDate)} at {formatTime(preferredTime)}
                </strong>
                . Cassie will confirm by email or text.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/" className="btn-primary">
                  Back to Home
                </Link>
                <button
                  type="button"
                  onClick={() => setSuccess(false)}
                  className="btn-secondary"
                >
                  Make Another Change
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    )
  }

  // -------------------------------------------------------------------
  // Loading (verifying token)
  // -------------------------------------------------------------------
  if (view === 'loading') {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Seo
          title="Manage My Consultation"
          description="Manage your consultation request with Cassie."
        />
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand" />
      </div>
    )
  }

  // -------------------------------------------------------------------
  // Invalid / expired token — offer to request a fresh link
  // -------------------------------------------------------------------
  if (view === 'invalid') {
    return (
      <div className="min-h-screen bg-cream">
        <Seo
          title="Manage My Consultation"
          description="Manage your consultation request with Cassie."
        />
        <section className="section-padding">
          <div className="container-max max-w-xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl shadow-soft border border-gray-100 p-8 sm:p-10"
            >
              <h1 className="heading-section mb-3">Link expired</h1>
              <p className="text-gray-600 mb-8">{error}</p>
              <EmailRequestForm
                emailInput={emailInput}
                setEmailInput={setEmailInput}
                emailSubmitted={emailSubmitted}
                emailLoading={emailLoading}
                emailError={emailError}
                onSubmit={handleRequestLink}
              />
            </motion.div>
          </div>
        </section>
      </div>
    )
  }

  // -------------------------------------------------------------------
  // No token in URL — ask for email to receive a management link
  // -------------------------------------------------------------------
  if (view === 'needEmail') {
    return (
      <div className="min-h-screen bg-cream">
        <Seo
          title="Manage My Consultation"
          description="Request a secure link to manage your consultation request with Cassie."
        />
        <section className="section-padding">
          <div className="container-max max-w-xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl shadow-soft border border-gray-100 p-8 sm:p-10"
            >
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-brand-soft">
                <Mail className="text-brand" size={24} />
              </div>
              <h1 className="heading-section mb-3 text-center">
                Manage My Consultation
              </h1>
              <p className="text-gray-600 mb-8 text-center">
                Enter the email you used to book, and we&apos;ll send you a
                secure link to change or cancel your consultation request.
              </p>
              <EmailRequestForm
                emailInput={emailInput}
                setEmailInput={setEmailInput}
                emailSubmitted={emailSubmitted}
                emailLoading={emailLoading}
                emailError={emailError}
                onSubmit={handleRequestLink}
              />
            </motion.div>
          </div>
        </section>
      </div>
    )
  }

  // -------------------------------------------------------------------
  // Ready — show the edit form, pre-filled
  // -------------------------------------------------------------------
  if (!record) return null

  return (
    <div className="min-h-screen bg-cream">
      <Seo
        title="Manage My Consultation"
        description="Change or cancel your consultation request with Cassie."
      />
      <section className="section-padding">
        <div className="container-max max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-soft border border-gray-100 p-6 sm:p-10"
          >
            <h1 className="heading-section mb-1">Manage My Consultation</h1>
            <p className="text-gray-500 mb-8">
              Hi {record.fullName.split(' ')[0] || record.fullName}, update
              your preferred time below, or cancel your request.
            </p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm mb-6">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
              <div>
                <label className="form-label">Preferred Date</label>
                <DatePicker
                  value={preferredDate}
                  onChange={(date) => setPreferredDate(date)}
                  placeholder="Select a date"
                  className="form-input !border-gray-200 !rounded-xl !py-3.5"
                  min={minDate}
                />
              </div>
              <div>
                <label htmlFor="preferred_time" className="form-label">
                  Preferred Time (ET)
                </label>
                <select
                  id="preferred_time"
                  value={preferredTime}
                  onChange={(e) => setPreferredTime(e.target.value)}
                  className="form-input"
                >
                  <option value="">Select a 30-min slot</option>
                  {TIME_SLOTS.map((slot) => (
                    <option key={slot.value} value={slot.value}>
                      {slot.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={loading}
                className="btn-primary flex-1 !py-3.5"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mx-auto" />
                ) : (
                  <>
                    Save Changes
                    <ArrowRight className="ml-2" size={18} />
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={loading}
                className="btn-secondary !border-red-200 !text-red-600 hover:!bg-red-50"
              >
                Cancel Request
              </button>
            </div>

            <p className="text-center text-sm text-gray-500 mt-6">
              <Link to="/" className="text-brand font-semibold hover:text-brand-dark">
                Back to Home
              </Link>
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

// A small shared sub-form for requesting a fresh management link by email.
const EmailRequestForm: React.FC<{
  emailInput: string
  setEmailInput: (value: string) => void
  emailSubmitted: boolean
  emailLoading: boolean
  emailError: string
  onSubmit: (e: React.FormEvent) => void
}> = ({
  emailInput,
  setEmailInput,
  emailSubmitted,
  emailLoading,
  emailError,
  onSubmit,
}) => {
  if (emailSubmitted) {
    return (
      <div className="bg-brand-soft border border-brand/20 text-ink px-5 py-4 rounded-xl text-sm text-center">
        If we found a matching request, a secure link is on its way to your
        inbox. It&apos;s valid for 24 hours — check your spam folder if you
        don&apos;t see it shortly.
      </div>
    )
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {emailError && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
          {emailError}
        </div>
      )}
      <div>
        <label htmlFor="manage_email" className="form-label">
          Email Address
        </label>
        <input
          id="manage_email"
          type="email"
          required
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
          className="form-input"
          placeholder="you@example.com"
        />
      </div>
      <button
        type="submit"
        disabled={emailLoading}
        className="w-full btn-primary !py-3.5"
      >
        {emailLoading ? (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mx-auto" />
        ) : (
          'Email Me a Secure Link'
        )}
      </button>
    </form>
  )
}

export default ManageConsultation
